import moment, { Moment } from 'moment';
import * as R from 'ramda';
import Lewis from '../assets/images/lewis.png';

import HCF from '../assets/images/hcf.png';
import HCF_LOGO from '../assets/images/hcf_logo.png';
import { IDropdownOptions } from '../models';

interface JSONData {
  [key: string]: any;
}

export const stringAvatar = (name: any, avatarSx?: any) => {
  const sx = {
    width: 32,
    height: 32,
    fontSize: 'small',
    textTransform: 'uppercase',
    ...avatarSx,
  };
  if (name && name !== 'unknown') {
    const nameSplit = name.split(' ');

    return {
      sx,
      children: `${nameSplit[0][0]}${nameSplit.length > 1 ? nameSplit[1][0] : ''}`,
    };
  } else
    return {
      sx,
    };
};

export const generateRandonNumber = () => {
  const crypto = window.crypto;
  return crypto.randomUUID();
};

const isUnauthorizedError = R.pathEq(['response', 'status'], 401);
const isForbiddenError = R.pathEq(['response', 'status'], 403);
const isServerError = R.pathEq(['response', 'status'], 500);
const isBadRequest = R.pathEq(['response', 'status'], 400);
const isNotFound = R.pathEq(['response', 'status'], 404);
const isIntercationError = R.pathEq(['errorCode'], 'interaction_required');

const constants = {
  emailRegex: /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i,
  welcomeTitle: 'Job done right',
  welcomeSubtitle: '',
  MULTIPLE_CUSTOMER_ROLES_NOT_ALLOWED: 'MULTIPLE_CUSTOMER_ROLES_NOT_ALLOWED',
};

export const statusDropdown: IDropdownOptions[] = [
  {
    id: 1,
    text: 'Active',
  },
  {
    id: 0,
    text: 'Inactive',
  },
];
export const configStatusDropdown: any[] = [
  {
    id: 0,
    text: 'New',
  },
  {
    id: 1,
    text: 'Configuration',
  },
  {
    id: 2,
    text: 'Live',
  },
  {
    id: 3,
    text: 'On Hold',
  },
  {
    id: 4,
    text: 'Closed',
  },
];

export const formatFileSize = (bytes: number, decimalPoint: number) => {
  if (bytes === 0) return '0 Bytes';
  var k = 1000,
    dm = decimalPoint || 2,
    sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
    i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const getUrlExtension = (url: any) => {
  return url.split(/[#?]/)[0].split('.').pop().trim();
};

export const formatHourDisplay = (value: number) => {
  return value < 24
    ? `${moment.duration(value, 'hour').hours()}h  ${moment.duration(value, 'hour').minutes()}m `
    : `${value || 0}h 0m`;
};
export const formatToTwoDecimals = (value: number | string): string => {
  return Number(value).toFixed(2);
};

export const formatDecimalHoursIntoDisplay = (value: number) => {
  if (!value && value !== 0) return '-';

  const duration = moment.duration(value, 'hours');
  const hours = Math.floor(duration.asHours());
  const minutes = duration.minutes();

  return `${hours}h ${minutes}m`;
};

export const formatMinutesToHours = (value: number) => {
  if (!value && value !== 0) return '-';

  const hours = Math.floor(value / 60);
  const minutes = value % 60;

  return `${hours}h ${minutes}min`;
};

export const formatMilisecondsIntoHoursDisplay = (value: number) => {
  const duration = moment.duration(value, 'milliseconds');
  const hours = Math.floor(duration.asHours());
  const mins = Math.floor(duration.asMinutes()) - hours * 60;
  return `${hours}h  ${mins}m `;
};

const allowNumberAndTwoDecimalDigit = (e: any, value: string) => {
  // Allow backspace, delete, tab, and decimal point
  if (
    e.keyCode === 8 ||
    e.keyCode === 46 ||
    e.keyCode === 9 ||
    e.keyCode === 190 ||
    e.keyCode === 110 ||
    e.keyCode === 37 ||
    e.keyCode === 39
  ) {
    return true;
  }
  // Check if there are already two decimal places
  var decimalIndex = value.indexOf('.');
  if (
    decimalIndex >= 0 &&
    value.substring(decimalIndex + 1).length >= 2 &&
    e.target.selectionStart > decimalIndex
  ) {
    e.preventDefault();
    return false;
  }
  // Allow only digits from 0 to 9
  if ((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 96 && e.keyCode <= 105)) {
    return true;
  } else {
    e.preventDefault();
    return false;
  }
};

const allowOnlyNumber = (e: any) => {
  // Get the key code of the pressed key
  const keyCode = e.keyCode || e.which;

  // Allow backspace (key code 8) and delete (key code 46)
  if (keyCode === 8 || keyCode === 46) {
    return; // Allow backspace and delete
  }
  // Allow only numbers (key codes 48 to 57 correspond to numbers 0 to 9)
  // and also allow numbers from the numeric keypad (key codes 96 to 105)
  if ((keyCode >= 48 && keyCode <= 57) || (keyCode >= 96 && keyCode <= 105)) {
    return; // Allow numbers
  }
  e.preventDefault(); // Prevent the default action (typing the character)
};

const isHTMLValid = (htmlString: string) => {
  if (!htmlString) {
    return false; // return false if the HTML string is null or empty
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // check if the HTML string contains any content
  const hasContent =
    doc.documentElement.textContent && doc.documentElement.textContent.trim().length > 0;
  if (!hasContent) {
    return false;
  }

  // check if there are any parser errors
  return doc.documentElement.nodeName !== 'parsererror'; // returns true if the HTML is valid, false otherwise
};

const createDistinctArray = (objects: any, propertyName: string) => {
  const seenValues = new Set();
  return objects.filter(
    (obj: any) => !seenValues.has(obj[propertyName]) && seenValues.add(obj[propertyName])
  );
};

const weekDayDateFormat = (date: Moment) => {
  return moment(date).format('ddd, MMM DD, YYYY');
};

const simpleDateFormat = (date: Moment) => {
  return date.format('MM-DD-YYYY');
};

const compareJSON = (obj1: JSONData, obj2: JSONData) => {
  if (!obj1 || !obj2) {
    return;
  }
  // Check if the keys are the same
  const keys1 = Object.keys(obj1);

  // Check if the values are the same
  for (const key of keys1) {
    const value1 = obj1[key];
    const value2 = obj2[key];

    // Check if the values are objects and recursively compare them
    if (typeof value1 === 'object' && typeof value2 === 'object') {
      if (compareJSON(value1, value2)) {
        console.log(key);
        return true;
      }
    } else {
      // eslint-disable-next-line
      if (value1 != value2) {
        console.log(key);
        return true;
      }
    }
  }

  return false;
};

// Function to check for overlapping breaks
const checkForOverlappingBreaks = (data: any) => {
  const overlappingBreaks = [];
  // Sort the rows by breakStartTime
  const sortedRows = data.sort((a: any, b: any) => a.breakStartTime - b.breakStartTime);

  // Iterate over the sorted rows and check for overlaps
  for (let i = 0; i < sortedRows.length; i++) {
    const currentRow = sortedRows[i];
    const currentEnd = moment(currentRow.breakStartTime).add(currentRow.duration, 'hours');

    for (let j = i + 1; j < sortedRows.length; j++) {
      const nextRow = sortedRows[j];
      console.log(moment(currentRow.breakStartTime).format('DD-MM-YYYY'));
      if (
        moment(currentRow.breakStartTime).format('DD-MM-YYYY') ===
        moment(nextRow.breakStartTime).format('DD-MM-YYYY')
      ) {
        const nextStart = moment(nextRow.breakStartTime).add(1, 'seconds');
        const nextEnd = moment(nextRow.breakStartTime).add(nextRow.duration, 'hours');
        if (
          moment(nextStart).isBetween(currentRow.breakStartTime, currentEnd) ||
          moment(nextEnd).isBetween(currentRow.breakStartTime, currentEnd)
        ) {
          overlappingBreaks.push({ currentRow, nextRow });
        }
      }
    }
  }
  return overlappingBreaks;
};

const capitalizeFirstLetter = (string: string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const assetTypes = ['billableAsset', 'nonBillableAsset', 'goBackNonBillableAsset', 'totalAsset'];

const weekdaysInEnglish = moment
  .localeData('en')
  .weekdays()
  .map((day) => day.toLowerCase());

const dateISOString = 'YYYY-MM-DDTHH:mm:ss[Z]';

const cutOverlayCircle = (overlay: HTMLElement, target: HTMLElement) => {
  const rect = target.getBoundingClientRect();

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;

  const r = Math.max(rect.width, rect.height) / 2 + 12;
  overlay.classList.remove('square');
  overlay.classList.add('circle');
  overlay.style.setProperty('--cx', `${cx}px`);
  overlay.style.setProperty('--cy', `${cy}px`);
  overlay.style.setProperty('--r', `${r}px`);
};

const cutOverlaySquare = (overlay: HTMLElement, target: HTMLElement, padding = 8) => {
  const rect = target.getBoundingClientRect();
  overlay.classList.add('square');
  overlay.style.setProperty('--left', `${rect.left - padding}px`);
  overlay.style.setProperty('--top', `${rect.top - padding}px`);
  overlay.style.setProperty('--right', `${rect.right + padding}px`);
  overlay.style.setProperty('--bottom', `${rect.bottom + padding}px`);
};
type Position = {
  x: number;
  y: number;
};

const exportCroppedImage = async (
  imageSrc: string,
  scale: number,
  rotation: number,
  position: Position
): Promise<string> => {
  const CROP_SIZE = 150;

  const canvas = document.createElement('canvas');
  canvas.width = CROP_SIZE;
  canvas.height = CROP_SIZE;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');

  const image = new Image();
  image.src = imageSrc;
  await new Promise((res) => (image.onload = res));

  ctx.clearRect(0, 0, CROP_SIZE, CROP_SIZE);

  // 🔵 Circular clip
  ctx.save();
  ctx.beginPath();
  ctx.arc(75, 75, 75, 0, Math.PI * 2);
  ctx.clip();

  // 🔵 Move origin to center
  ctx.translate(75, 75);

  // 🔵 Rotate (rotation does not affect movement scale)
  ctx.rotate((rotation * Math.PI) / 180);

  // 🔵 Apply drag movement (screen pixels)
  ctx.translate(position.x, position.y);

  // 🔵 Apply zoom by resizing image (NOT ctx.scale)
  const baseWidth = 150;
  const baseHeight = (image.naturalHeight / image.naturalWidth) * baseWidth;

  const zoomedWidth = baseWidth * scale;
  const zoomedHeight = baseHeight * scale;

  ctx.drawImage(image, -zoomedWidth / 2, -zoomedHeight / 2, zoomedWidth, zoomedHeight);

  ctx.restore();

  return canvas.toDataURL('image/png');
};

const dataUrlToBlob = (dataUrl: string): Blob => {
  const [header, base64] = dataUrl.split(',');

  let mime = 'image/png';
  if (header.startsWith('data:')) {
    const semiColonIndex = header.indexOf(';');
    if (semiColonIndex !== -1) {
      mime = header.substring(5, semiColonIndex); // remove 'data:'
    }
  }

  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: mime });
};

const dataUrlToFile = (dataUrl: string, fileName: string): File => {
  const blob = dataUrlToBlob(dataUrl);
  return new File([blob], fileName, { type: blob.type });
};

const getInitials = (name: string) => {
  return name && name.split(' ').length > 1
    ? name.split(' ')[0][0] + name.split(' ')[1][0]
    : name[0][0];
};

export {
  allowNumberAndTwoDecimalDigit,
  allowOnlyNumber,
  assetTypes,
  capitalizeFirstLetter,
  checkForOverlappingBreaks,
  compareJSON,
  constants,
  createDistinctArray,
  cutOverlayCircle,
  cutOverlaySquare,
  dataUrlToFile,
  dateISOString,
  exportCroppedImage,
  getInitials,
  isBadRequest,
  isForbiddenError,
  isHTMLValid,
  isIntercationError,
  isNotFound,
  isServerError,
  isUnauthorizedError,
  simpleDateFormat,
  weekDayDateFormat,
  weekdaysInEnglish,
};

// Place this function above your component or in a utils file
export const getCompanyLogo = (id: string, isFromMySpace = false) => {
  switch (id) {
    case 'HighCountryFenceLLC':
    case 'High Country Fence LLC':
      if (isFromMySpace) {
        return HCF_LOGO;
      }
      return HCF;
    case 'LewisTreeServiceInc':
    case 'Lewis Tree Service Inc.':
      return Lewis;
    // Add more cases as needed
    default:
      return Lewis;
  }
};

export enum CompanyEnum {
  HighCountryFenceLLC = 'HighCountryFenceLLC',
  LewisTreeServiceInc = 'LewisTreeServiceInc',
  // Add more as needed
}

export enum CompanyIdEnum {
  HighCountryFenceLLC = 2,
  LewisTreeServiceInc = 1,
  // Add more as needed
}

export const CompanyDisplayName: Record<CompanyEnum, string> = {
  [CompanyEnum.HighCountryFenceLLC]: 'High Country Fence LLC',
  [CompanyEnum.LewisTreeServiceInc]: 'Lewis Tree Service Inc.',
  // Add more as needed
};

export enum Environment {
  DEV = 'Dev',
  QA = 'QA',
  TEST = 'Test',
  PROD = 'Prod',
}

export const getCompanyFavicon = (id: string) => {
  switch (id) {
    case 'HighCountryFenceLLC':
    case 'High Country Fence LLC':
      return '/hcf-favicon.ico';
    case 'LewisTreeServiceInc':
    case 'Lewis Tree Service Inc.':
      return '/lewis-favicon.ico';
    // Add more cases as needed
    default:
      return '/lewis-favicon.ico';
  }
};
export const updateFavicon = () => {
  const selectedCompany = localStorage.getItem('selectedCompany') || 'Lewis Tree Service Inc.';
  // Remove all favicon links
  const links = document.querySelectorAll("link[rel~='icon']");
  links.forEach((link) => link.parentNode?.removeChild(link));

  // Add the new favicon
  const link = document.createElement('link');
  link.id = 'app-favicon';
  link.rel = 'icon';
  link.type = 'image/x-icon';
  link.href = getCompanyFavicon(selectedCompany || '');
  document.head.appendChild(link);
};
